import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs, setDoc } from '../../libs/firestore-wrapper';
import { IGameSchedule } from '../../pages/GameSchedule/gameSchedule.types';
import { useAuthContext } from '../AuthContext/AuthContext';
import { useSettingsContext } from '../SettingsContext';

interface GameScheduleContextValue {
  loading: boolean;
  gameSchedules: IGameSchedule[];
  addGameSchedule: (g: IGameSchedule) => void;
  updateGameSchedule: (id: string, data: Partial<IGameSchedule>) => Promise<void>;
  removeGameSchedule: (id: string) => void;
  importGameSchedules: (rows: IGameSchedule[]) => Promise<void>;
}

const GameScheduleContext = createContext<GameScheduleContextValue>({
  loading: true,
  gameSchedules: [],
  addGameSchedule: () => { },
  updateGameSchedule: async () => { },
  removeGameSchedule: () => { },
  importGameSchedules: async () => { },
});

export const GameScheduleProvider = ({ children }: { children: ReactNode }) => {
  const { userInfo } = useAuthContext();
  const ROOT_COLLECTION = 'stadiums_gamedays';
  const { currentSetting } = useSettingsContext();

  const stadiumId = userInfo?.stadium_id || currentSetting?.stadium_id || '';
  const [gameSchedules, setGameSchedules] = useState<IGameSchedule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGameSchedules = async () => {
      if (!stadiumId) return;

      try {
        setLoading(true);
        const colRef = collection(null as any, ROOT_COLLECTION, stadiumId, 'gamedays');
        const snap = await getDocs(colRef);
        const rows: IGameSchedule[] = snap.docs.map((d: any) => ({ ...(d.data() as IGameSchedule), id: d.id }));
        rows.sort((a, b) => a.date?.localeCompare(b.date || '') || 0);
        setGameSchedules(rows);
      } catch (err) {
        console.error('Failed to fetch gamedays:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGameSchedules();
  }, [stadiumId]);

  const addGameSchedule = async (g: IGameSchedule) => {
    if (!stadiumId) {
      setGameSchedules((prev) => [...prev, g].sort((a, b) => (a.date || '').localeCompare(b.date || '')));
      return;
    }

    try {
      try {
        const parentRef = doc(null as any, ROOT_COLLECTION, stadiumId);
        const parentSnap = await getDoc(parentRef as any);
        if (!parentSnap.exists()) {
          await setDoc(parentRef, { createdAt: new Date(), updatedAt: new Date() }, { merge: true });
        }
      } catch (err) {
        console.warn('Failed to ensure parent stadium doc exists:', err);
      }

      const id = g.date || g.id;
      const docRef = doc(null as any, ROOT_COLLECTION, stadiumId, 'gamedays', id);
      const writeObj: any = {};
      Object.entries({ ...g, date: g.date }).forEach(([k, v]) => {
        if (v !== undefined) writeObj[k] = v;
      });
      await setDoc(docRef, { ...writeObj, updatedAt: new Date() }, { merge: true });
      setGameSchedules((prev) => [...prev.filter((x) => x.id !== id), { ...g, id }].sort((a, b) => (a.date || '').localeCompare(b.date || '')));
    } catch (err) {
      console.error('Failed to add gameday:', err);
    }
  };

  const updateGameSchedule = async (id: string, data: Partial<IGameSchedule>) => {
    if (!stadiumId) {
      setGameSchedules((prev) => prev.map((g) => (g.id === id ? { ...g, ...data } : g)).sort((a, b) => (a.date || '').localeCompare(b.date || '')));
      return;
    }

    try {
      const parentRef = doc(null as any, ROOT_COLLECTION, stadiumId);
      const parentSnap = await getDoc(parentRef as any);
      if (!parentSnap.exists()) {
        await setDoc(parentRef, { createdAt: new Date(), updatedAt: new Date() }, { merge: true });
      }

      const docRef = doc(null as any, ROOT_COLLECTION, stadiumId, 'gamedays', id);
      const writeData: any = { updatedAt: new Date() };
      Object.entries(data).forEach(([k, v]) => {
        if (v !== undefined) writeData[k] = v;
      });

      await setDoc(docRef, writeData, { merge: true });

      setGameSchedules((prev) => {
        const index = prev.findIndex((g) => g.id === id);
        const updated = index >= 0 ? prev.map((g) => (g.id === id ? { ...g, ...data } : g)) : [...prev, { ...data, id } as IGameSchedule];
        return updated.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      });
    } catch (err) {
      console.error('Failed to update gameday:', err);
      throw err;
    }
  };

  const removeGameSchedule = async (id: string) => {
    if (!stadiumId) {
      setGameSchedules((prev) => prev.filter((g) => g.id !== id));
      return;
    }

    try {
      const docRef = doc(null as any, ROOT_COLLECTION, stadiumId, 'gamedays', id);
      await deleteDoc(docRef);
      setGameSchedules((prev) => prev.filter((g) => g.id !== id));
    } catch (err) {
      console.error('Failed to remove gameday:', err);
    }
  };

  const normalizeDate = (rawDate: string) => {
    const d = (rawDate || '').trim();
    if (!d) return '';
    if (d.includes('/')) {
      const parts = d.split('/').map((s) => s.trim());
      if (parts.length === 3) {
        const [m, day, y] = parts;
        const mm = m.padStart(2, '0');
        const dd = day.padStart(2, '0');
        return `${y}-${mm}-${dd}`;
      }
      return d.replace(/\//g, '-');
    }
    const parts = d.split('-').map((s) => s.trim());
    if (parts.length === 3 && parts[0].length === 4) return d; // yyyy-mm-dd
    return d;
  };

  const importGameSchedules = async (rows: IGameSchedule[]) => {
    if (!rows || rows.length === 0) return;

    try {
      if (stadiumId) {
        try {
          const parentRef = doc(null as any, ROOT_COLLECTION, stadiumId);
          const parentSnap = await getDoc(parentRef as any);
          if (!parentSnap.exists()) {
            await setDoc(parentRef, { createdAt: new Date(), updatedAt: new Date() }, { merge: true });
          }
        } catch (err) {
          console.warn('Failed to create parent stadium doc:', err);
        }
      }

      const map = new Map<string, IGameSchedule>(gameSchedules.map((g) => [g.date, g]));

      for (const r of rows) {
        if (!r) continue;
        const dateStr = normalizeDate(r.date);
        if (!dateStr) continue;

        const docId = dateStr;
        const payload: IGameSchedule = { ...r, id: docId, date: dateStr };

        if (stadiumId) {
          const docRef = doc(null as any, ROOT_COLLECTION, stadiumId, 'gamedays', docId);
          const writeObj: any = {};
          Object.entries(payload).forEach(([k, v]) => {
            if (v !== undefined) writeObj[k] = v;
          });
          await setDoc(docRef, { ...writeObj, updatedAt: new Date() }, { merge: true });
        }

        map.set(dateStr, payload);
      }

      const merged = Array.from(map.values()).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setGameSchedules(merged);
    } catch (err) {
      console.error('Failed to import gamedays:', err);
    }
  };

  return (
    <GameScheduleContext.Provider value={{ gameSchedules, loading, addGameSchedule, updateGameSchedule, removeGameSchedule, importGameSchedules }}>
      {children}
    </GameScheduleContext.Provider>
  );
};

export const useGameScheduleContext = () => useContext(GameScheduleContext);

export default GameScheduleContext;
